/**
 * Cosmetics paid in hearts: VIP status, the table's bottle and avatar frames ("styling"). Prices live here so the
 * client can show them; the server is the only authority that charges them.
 */

/** VIP status. Buying again extends the current period. */
export const VIP_PLANS = [
  { code: 'vip_7', days: 7, price: 300 },
  { code: 'vip_30', days: 30, price: 900 },
] as const;

export type VipPlanCode = (typeof VIP_PLANS)[number]['code'];

export function findVipPlan(code: string) {
  return VIP_PLANS.find((p) => p.code === code);
}

export function isVipActive(vipUntil: string | number | Date | null | undefined, now = Date.now()): boolean {
  return vipUntil != null && new Date(vipUntil).getTime() > now;
}

/** Shapes the bottle artwork can take; colours come from the skin. */
export type BottleShape = 'bottle' | 'flask' | 'baby';

export interface BottleSkin {
  code: string;
  /** Hearts to put this bottle on the table (0 = the default bottle). */
  price: number;
  vipOnly?: boolean;
  shape: BottleShape;
  /** Glass gradient: edge, body, highlight. */
  glass: readonly [string, string, string];
  /** Cap or foil colour. */
  foil: string;
  /** Label background, or null for a bottle without a label. */
  label: string | null;
  /** Symbol printed on the label. */
  mark: string;
  markColor: string;
}

export const DEFAULT_BOTTLE = 'classic';

export const BOTTLE_SKINS: readonly BottleSkin[] = [
  {
    code: 'classic',
    price: 0,
    shape: 'bottle',
    glass: ['#1e4a14', '#4c9a28', '#bde89c'],
    foil: '#d9b45a',
    label: null,
    mark: '',
    markColor: '#1e4a14',
  },
  {
    code: 'emerald',
    price: 3,
    shape: 'bottle',
    glass: ['#0b4d2c', '#1fa463', '#8ff0bf'],
    foil: '#ffd36b',
    label: '#fff7dc',
    mark: '❦',
    markColor: '#0b6b3a',
  },
  {
    code: 'amber',
    price: 3,
    shape: 'bottle',
    glass: ['#4a2306', '#a8571a', '#f0b27a'],
    foil: '#d63b2f',
    label: '#f6e7c8',
    mark: '★',
    markColor: '#a8571a',
  },
  {
    code: 'champagne',
    price: 5,
    shape: 'bottle',
    glass: ['#14301f', '#2f6b45', '#9fd4b1'],
    foil: '#f2c14e',
    label: '#1d1d1d',
    mark: '✦',
    markColor: '#f2c14e',
  },
  {
    code: 'cola',
    price: 5,
    shape: 'bottle',
    glass: ['#2a0b06', '#6b1d10', '#c06a52'],
    foil: '#e8e8e8',
    label: '#e3101e',
    mark: '~',
    markColor: '#ffffff',
  },
  {
    code: 'lemonade',
    price: 3,
    shape: 'bottle',
    glass: ['#5d7a12', '#b5d334', '#f4ffb0'],
    foil: '#ffe14d',
    label: '#ffffff',
    mark: '◐',
    markColor: '#d4a700',
  },
  {
    code: 'whisky',
    price: 5,
    shape: 'bottle',
    glass: ['#3b1d03', '#b8671b', '#ffd08a'],
    foil: '#111111',
    label: '#111111',
    mark: 'Ⅶ',
    markColor: '#f5f5f5',
  },
  {
    code: 'wine',
    price: 5,
    shape: 'bottle',
    glass: ['#2b0412', '#7a0f2f', '#d65b7f'],
    foil: '#5c0a20',
    label: '#f3e7d3',
    mark: '❧',
    markColor: '#7a0f2f',
  },
  {
    code: 'sparkle',
    price: 3,
    shape: 'bottle',
    glass: ['#0b1f6b', '#2f6bff', '#a9c7ff'],
    foil: '#e6e9ff',
    label: '#ffffff',
    mark: '✧',
    markColor: '#2f6bff',
  },
  {
    code: 'ice',
    price: 5,
    shape: 'bottle',
    glass: ['#3a7ca5', '#9fdcff', '#ffffff'],
    foil: '#d8f3ff',
    label: null,
    mark: '❄',
    markColor: '#3a7ca5',
  },
  {
    code: 'gold',
    price: 5,
    shape: 'bottle',
    glass: ['#7a5208', '#e8b22e', '#fff1b8'],
    foil: '#fff1b8',
    label: '#2b1a00',
    mark: '★',
    markColor: '#ffd36b',
  },
  {
    code: 'potion',
    price: 5,
    shape: 'flask',
    glass: ['#2e0b5c', '#8b3dff', '#e0c3ff'],
    foil: '#b07a3a',
    label: null,
    mark: '✺',
    markColor: '#ffffff',
  },
  {
    code: 'baby',
    price: 5,
    shape: 'baby',
    glass: ['#c9d6e8', '#f7fbff', '#ffffff'],
    foil: '#7fd3ff',
    label: null,
    mark: '·',
    markColor: '#7fd3ff',
  },
  {
    code: 'vip',
    price: 5,
    vipOnly: true,
    shape: 'bottle',
    glass: ['#050505', '#2a2a2a', '#7a7a7a'],
    foil: '#f2c14e',
    label: '#f2c14e',
    mark: 'VIP',
    markColor: '#111111',
  },
];

export function findBottleSkin(code: string | null | undefined): BottleSkin {
  return BOTTLE_SKINS.find((b) => b.code === code) ?? BOTTLE_SKINS[0]!;
}

/** How a frame is earned when it is not sold. */
export type FrameReward = 'league_champion' | 'kiss_master';

export interface FrameDef {
  code: string;
  /** Hearts, or null when the frame is a reward and cannot be bought. */
  price: number | null;
  vipOnly?: boolean;
  reward?: FrameReward;
  /** CSS background of the ring around the avatar. */
  ring: string;
  glow: string;
  /** Emoji on top of the avatar (crown, bow…). */
  top?: string;
  /** Emoji at the avatar's bottom-left corner. */
  corner?: string;
  /** Small symbol on both sides of the name. */
  nameIcon?: string;
}

export const FRAMES: readonly FrameDef[] = [
  {
    code: 'rose_garden',
    price: 300,
    ring: 'conic-gradient(#ff8fb8, #ff3d8b, #ffd1e3, #ff3d8b, #ff8fb8)',
    glow: 'rgba(255,61,139,.75)',
    corner: '🌹',
    nameIcon: '❀',
  },
  {
    code: 'neon_pink',
    price: 300,
    ring: 'conic-gradient(#ff00a8, #ff7ad9, #ff00a8, #ff7ad9, #ff00a8)',
    glow: 'rgba(255,0,168,.9)',
  },
  {
    code: 'neon_cyan',
    price: 300,
    ring: 'conic-gradient(#00e5ff, #8ff6ff, #00e5ff, #8ff6ff, #00e5ff)',
    glow: 'rgba(0,229,255,.85)',
  },
  {
    code: 'hearts',
    price: 300,
    ring: 'repeating-conic-gradient(#ff3d8b 0 20deg, #ffc2da 20deg 40deg)',
    glow: 'rgba(255,61,139,.7)',
    top: '💕',
    nameIcon: '♥',
  },
  {
    code: 'candy',
    price: 300,
    ring: 'repeating-conic-gradient(#ff5fa2 0 15deg, #ffffff 15deg 30deg)',
    glow: 'rgba(255,95,162,.7)',
    corner: '🍭',
  },
  {
    code: 'bow',
    price: 300,
    ring: 'conic-gradient(#ffd1e3, #ff8fb8, #ffffff, #ff8fb8, #ffd1e3)',
    glow: 'rgba(255,143,184,.75)',
    top: '🎀',
  },
  {
    code: 'leaves',
    price: 300,
    ring: 'conic-gradient(#c2410c, #f59e0b, #84cc16, #f59e0b, #c2410c)',
    glow: 'rgba(245,158,11,.7)',
    corner: '🍁',
    nameIcon: '❧',
  },
  {
    code: 'flame',
    price: 400,
    ring: 'conic-gradient(#ff4500, #ffb000, #ff4500, #ff8c00, #ff4500)',
    glow: 'rgba(255,90,0,.9)',
    corner: '🔥',
    nameIcon: '🔥',
  },
  {
    code: 'ice',
    price: 400,
    ring: 'conic-gradient(#e0f7ff, #7dd3fc, #ffffff, #38bdf8, #e0f7ff)',
    glow: 'rgba(125,211,252,.85)',
    corner: '❄️',
    nameIcon: '❄',
  },
  {
    code: 'butterfly',
    price: 400,
    ring: 'conic-gradient(#a855f7, #ec4899, #f0abfc, #ec4899, #a855f7)',
    glow: 'rgba(168,85,247,.8)',
    top: '🦋',
  },
  {
    code: 'emerald',
    price: 400,
    ring: 'conic-gradient(#065f46, #34d399, #a7f3d0, #34d399, #065f46)',
    glow: 'rgba(52,211,153,.8)',
    nameIcon: '◆',
  },
  {
    code: 'ruby',
    price: 400,
    ring: 'conic-gradient(#7f1d1d, #ef4444, #fecaca, #ef4444, #7f1d1d)',
    glow: 'rgba(239,68,68,.85)',
    nameIcon: '◆',
  },
  {
    code: 'sapphire',
    price: 400,
    ring: 'conic-gradient(#1e3a8a, #3b82f6, #bfdbfe, #3b82f6, #1e3a8a)',
    glow: 'rgba(59,130,246,.85)',
    nameIcon: '◆',
  },
  {
    code: 'devil',
    price: 400,
    ring: 'conic-gradient(#450a0a, #dc2626, #111111, #dc2626, #450a0a)',
    glow: 'rgba(220,38,38,.9)',
    top: '😈',
    nameIcon: '†',
  },
  {
    code: 'angel',
    price: 400,
    ring: 'conic-gradient(#ffffff, #fde68a, #ffffff, #fde68a, #ffffff)',
    glow: 'rgba(253,230,138,.9)',
    top: '😇',
    nameIcon: '✧',
  },
  {
    code: 'galaxy',
    price: 500,
    ring: 'conic-gradient(#3b0ca3, #8b5cf6, #22d3ee, #f0abfc, #3b0ca3)',
    glow: 'rgba(139,92,246,.9)',
    corner: '🪐',
    nameIcon: '✦',
  },
  {
    code: 'rainbow',
    price: 500,
    ring: 'conic-gradient(#ff3b30, #ff9500, #ffcc00, #34c759, #00c7be, #007aff, #af52de, #ff3b30)',
    glow: 'rgba(255,255,255,.65)',
    nameIcon: '✿',
  },
  {
    code: 'royal',
    price: 500,
    ring: 'conic-gradient(#ffe08a, #b8860b, #fff3c4, #b8860b, #ffe08a)',
    glow: 'rgba(255,201,77,.85)',
    top: '👑',
    nameIcon: '♛',
  },
  {
    code: 'vip_gold',
    price: 500,
    vipOnly: true,
    ring: 'conic-gradient(#111111, #f2c14e, #111111, #f2c14e, #111111)',
    glow: 'rgba(242,193,78,.95)',
    top: '💎',
    nameIcon: '★',
  },
  {
    code: 'vip_diamond',
    price: 700,
    vipOnly: true,
    ring: 'conic-gradient(#e0f2fe, #a5f3fc, #ffffff, #c4b5fd, #e0f2fe)',
    glow: 'rgba(165,243,252,.95)',
    top: '💠',
    nameIcon: '◇',
  },
  {
    code: 'league_champion',
    price: null,
    reward: 'league_champion',
    ring: 'conic-gradient(#f59e0b, #fde68a, #b45309, #fde68a, #f59e0b)',
    glow: 'rgba(245,158,11,.95)',
    top: '🏆',
    nameIcon: '♚',
  },
  {
    code: 'kiss_master',
    price: null,
    reward: 'kiss_master',
    ring: 'repeating-conic-gradient(#e11d48 0 30deg, #fb7185 30deg 60deg)',
    glow: 'rgba(225,29,72,.9)',
    corner: '💋',
    nameIcon: '❤',
  },
];

export function findFrame(code: string | null | undefined): FrameDef | undefined {
  return code ? FRAMES.find((f) => f.code === code) : undefined;
}

/**
 * Sticker packs for the table chat. Sending a sticker is a "gesture": it costs ECONOMY.gesturePrice, floats over the
 * sender's avatar and counts for the "most emotional" rating. Basic is everyone's; VIP needs VIP status.
 */
export interface StickerPack {
  code: string;
  /** Hearts to own the pack; 0 = everyone has it; null = VIP only. */
  price: number | null;
  stickers: readonly string[];
}

export const STICKER_PACKS: readonly StickerPack[] = [
  {
    code: 'basic',
    price: 0,
    stickers: ['😂', '😍', '😘', '🥰', '😎', '🤔', '😭', '👍', '👏', '🔥', '😡', '🙈'],
  },
  {
    code: 'love',
    price: 50,
    stickers: ['💋', '💘', '💞', '😻', '🌹', '💍', '💌', '🫶', '😚', '🥺', '💗', '🤗'],
  },
  {
    code: 'party',
    price: 50,
    stickers: ['🎉', '🥳', '🍾', '🕺', '💃', '🎶', '🍻', '🪩', '🎊', '🤩', '🥂', '🎤'],
  },
  {
    code: 'animals',
    price: 50,
    stickers: ['🐱', '🐶', '🐼', '🦊', '🐸', '🐵', '🦄', '🐧', '🐰', '🐨', '🦁', '🐹'],
  },
  {
    code: 'vip',
    price: null,
    stickers: ['👑', '💎', '🌟', '🥂', '🏆', '💸', '🎩', '🦚', '🛥️', '🏰', '🦋', '✨'],
  },
];

export function findStickerPack(code: string): StickerPack | undefined {
  return STICKER_PACKS.find((p) => p.code === code);
}
